import { z } from 'zod';
import { IdSchema, InstantSchema } from '../common/index.js';
import { MandateStatusSchema } from './dossier.js';

/** Documents (GED abstraite), pièces attendues, mandats et signatures (architecture §2.5). */

export const DocumentCategorySchema = z
  .enum(['etat_civil', 'titre', 'mandat', 'procuration', 'retour_notaire', 'courrier', 'facture', 'identite', 'autre'])
  .meta({ id: 'DocumentCategory' });

export const DocumentStatusSchema = z.enum(['recu', 'a_verifier', 'valide', 'refuse', 'non_signe']).meta({ id: 'DocumentStatus' });

export const PreviewSchema = z
  .object({
    thumbnailUrl: z.string().nullable(),
    pages: z.number().int().min(1).nullable(),
    durationS: z.number().min(0).nullable(),
  })
  .meta({ id: 'Preview', description: 'Aperçu : vignette, nombre de pages (PDF), durée (vidéo ou vocal).' });

export const DocumentVersionSchema = z
  .object({
    versionNo: z.number().int().min(1),
    mime: z.string().min(3),
    size: z.number().int().min(0),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    source: z.enum(['whatsapp', 'email', 'portail', 'docuseal', 'espace_clerc', 'rendez_vous']),
    preview: PreviewSchema,
    quality: z.object({ blurScore: z.number().min(0).nullable(), readable: z.boolean().nullable() }),
  })
  .meta({ id: 'DocumentVersion' });

export const DocumentSchema = z
  .object({
    id: IdSchema,
    dossierId: IdSchema,
    title: z.string().min(1),
    category: DocumentCategorySchema,
    status: DocumentStatusSchema,
    visibility: z.enum(['equipe', 'notaire', 'famille']),
    currentVersion: DocumentVersionSchema,
    links: z.array(z.object({ targetType: z.enum(['person', 'asset', 'succession', 'dossier']), targetId: IdSchema })),
    receivedAt: InstantSchema,
  })
  .meta({ id: 'Document', description: 'Pièce du dossier (équipe, clerc, notaire selon visibilité).' });

export const DocumentUploadSchema = z
  .object({
    title: z.string().min(1),
    category: DocumentCategorySchema,
    mime: z.string().min(3),
    size: z.number().int().min(1),
    links: z.array(z.object({ targetType: z.enum(['person', 'asset', 'succession', 'dossier']), targetId: IdSchema })),
  })
  .meta({ id: 'DocumentUpload', description: 'Ouverture d’un téléversement reprenable (tus).' });

export const UploadSessionSchema = z
  .object({ documentId: IdSchema, uploadUrl: z.string().min(1), expiresAt: InstantSchema })
  .meta({ id: 'UploadSession' });

export const DocumentReviewSchema = z
  .object({ decision: z.enum(['valider', 'meilleure_photo', 'refuser']), reason: z.string().optional() })
  .meta({ id: 'DocumentReview' });

export const DocumentRequirementSchema = z
  .object({
    id: IdSchema,
    personId: IdSchema.nullable(),
    assetId: IdSchema.nullable(),
    kind: z.enum(['acte_naissance', 'acte_mariage', 'acte_deces', 'livret', 'piece_identite', 'procuration', 'titre']),
    label: z.string().min(1),
    status: z.enum(['attendu', 'demande', 'recu', 'verifie', 'refuse']),
    requestedAt: InstantSchema.nullable(),
    delegatedRequest: z.boolean(),
  })
  .meta({ id: 'DocumentRequirement', description: 'Pièce attendue (checklist).' });

export const MandateSchema = z
  .object({
    id: IdSchema,
    dossierId: IdSchema,
    kind: z.enum(['mandat_recherche', 'devis', 'procuration', 'pouvoir']),
    signerPersonId: IdSchema,
    provider: z.literal('docuseal'),
    status: MandateStatusSchema,
    signedAt: InstantSchema.nullable(),
    documentId: IdSchema.nullable(),
  })
  .meta({ id: 'Mandate', description: 'Mandat, devis ou procuration ; aucune recherche sans mandat de recherche signé.' });

export const MandateSendSchema = z
  .object({ kind: z.enum(['mandat_recherche', 'devis', 'procuration', 'pouvoir']), signerPersonId: IdSchema })
  .meta({ id: 'MandateSend' });
